import { useEffect, useMemo } from 'react'
import { useForm, Controller } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useTranslation } from 'react-i18next'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { useToast } from '@/hooks/use-toast'
import { useUpdateTenant } from './hooks/useUpdateTenant'
import {
  getUpdateTenantSchema,
  type UpdateTenantFormData,
} from './schemas/updateTenantSchema'
import type { Tenant } from '@/types/auth'

interface EditTenantDialogProps {
  tenant: Tenant | null
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function EditTenantDialog({ tenant, open, onOpenChange }: EditTenantDialogProps) {
  const { t } = useTranslation()
  const { toast } = useToast()
  const updateTenant = useUpdateTenant()

  const schema = useMemo(() => getUpdateTenantSchema(t), [t])

  const statusOptions = [
    { value: 'active', label: t('admin.tenants.status.active') },
    { value: 'suspended', label: t('admin.tenants.status.suspended') },
    { value: 'pending', label: t('admin.tenants.status.pending') },
    { value: 'cancelled', label: t('admin.tenants.status.cancelled') },
  ] as const

  const {
    register,
    handleSubmit,
    reset,
    control,
    formState: { errors },
  } = useForm<UpdateTenantFormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      id: '',
      name: '',
      slug: '',
      domain: '',
      logoUrl: '',
      status: 'active',
    },
  })

  // Reset form when tenant changes
  useEffect(() => {
    if (tenant) {
      reset({
        id: tenant.id,
        name: tenant.name,
        slug: tenant.slug,
        domain: tenant.domain || '',
        logoUrl: tenant.logo_url || '',
        status: tenant.status,
      })
    }
  }, [tenant, reset])

  const onSubmit = async (data: UpdateTenantFormData) => {
    try {
      await updateTenant.mutateAsync({
        id: data.id,
        name: data.name,
        slug: data.slug,
        domain: data.domain || null,
        logoUrl: data.logoUrl || null,
        status: data.status,
      })

      toast({
        title: t('admin.tenants.edit.success'),
        description: t('admin.tenants.edit.successMessage', { name: data.name }),
      })

      onOpenChange(false)
    } catch (error) {
      toast({
        variant: 'destructive',
        title: t('admin.tenants.edit.error'),
        description: error instanceof Error ? error.message : t('errors.unknown'),
      })
    }
  }

  const handleClose = () => {
    if (!updateTenant.isPending) {
      onOpenChange(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>{t('admin.tenants.edit.title')}</DialogTitle>
          <DialogDescription>
            {t('admin.tenants.edit.description')}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <input type="hidden" {...register('id')} />

          <div className="space-y-2">
            <Label htmlFor="name">{t('admin.tenants.create.companyName')} *</Label>
            <Input
              id="name"
              placeholder={t('admin.tenants.create.companyNamePlaceholder')}
              {...register('name')}
              disabled={updateTenant.isPending}
            />
            {errors.name && (
              <p className="text-sm text-destructive">{errors.name.message}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="slug">{t('admin.tenants.create.slug')} *</Label>
            <Input
              id="slug"
              placeholder={t('admin.tenants.create.slugPlaceholder')}
              {...register('slug')}
              disabled={updateTenant.isPending}
            />
            {errors.slug && (
              <p className="text-sm text-destructive">{errors.slug.message}</p>
            )}
            <p className="text-xs text-muted-foreground">
              {t('admin.tenants.create.slugHelp')}
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="status">{t('admin.tenants.status.label')} *</Label>
            <Controller
              name="status"
              control={control}
              render={({ field }) => (
                <Select
                  value={field.value}
                  onValueChange={field.onChange}
                  disabled={updateTenant.isPending}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {statusOptions.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            />
            {errors.status && (
              <p className="text-sm text-destructive">{errors.status.message}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="domain">{t('admin.tenants.create.domain')}</Label>
            <Input
              id="domain"
              placeholder={t('admin.tenants.create.domainPlaceholder')}
              {...register('domain')}
              disabled={updateTenant.isPending}
            />
            {errors.domain && (
              <p className="text-sm text-destructive">{errors.domain.message}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="logoUrl">{t('admin.tenants.create.logoUrl')}</Label>
            <Input
              id="logoUrl"
              placeholder={t('admin.tenants.create.logoUrlPlaceholder')}
              {...register('logoUrl')}
              disabled={updateTenant.isPending}
            />
            {errors.logoUrl && (
              <p className="text-sm text-destructive">{errors.logoUrl.message}</p>
            )}
          </div>

          <div className="rounded-md bg-muted p-3 text-sm">
            <p className="text-muted-foreground">
              <strong>{t('admin.tenants.schema')}:</strong> {tenant?.schema_name}
            </p>
            <p className="text-xs text-muted-foreground mt-1">
              {t('admin.tenants.edit.schemaNote')}
            </p>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={updateTenant.isPending}
            >
              {t('common.cancel')}
            </Button>
            <Button type="submit" disabled={updateTenant.isPending}>
              {updateTenant.isPending ? t('common.saving') : t('common.save')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
